import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadSimple, DownloadSimple, FileText, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ErrorBox, VoidForm, errMsg, fmtDate } from "./shared";

const MAX = 15 * 1024 * 1024;
const OK_EXT = /\.(pdf|docx|doc)$/i;
const fmtSize = (n) => (n == null ? "" : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

export default function ScopeDocuments({ recipeId, documents = [], onReload, library = false }) {
  const endpoint = library ? "/rd/documents" : `/rd/recipes/${recipeId}/documents`;
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [archiving, setArchiving] = useState(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const pick = (f) => {
    setError("");
    if (!f) { setFile(null); return; }
    if (!OK_EXT.test(f.name)) { setError("Only PDF, DOCX or DOC files can be uploaded."); setFile(null); return; }
    if (f.size > MAX) { setError(`File is ${fmtSize(f.size)}; the limit is 15 MB.`); setFile(null); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setProgress(0); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(endpoint, fd, {
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      toast.success(`Uploaded ${file.name}`);
      setFile(null); if (inputRef.current) inputRef.current.value = "";
      await onReload();
    } catch (err) { setError(`Upload failed: ${errMsg(err)} The file is still selected — try again.`); }
    finally { setUploading(false); }
  };

  const download = async (d) => {
    setDownloading(d.id);
    try {
      const res = await api.get(`${endpoint}/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(`Download failed: ${errMsg(err)}`); }
    finally { setDownloading(null); }
  };

  const archive = async (d, reason) => {
    setArchiveBusy(true);
    try {
      await api.post(`${endpoint}/${d.id}/archive`, { reason });
      toast.success("Document archived — original retained");
      setArchiving(null); await onReload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setArchiveBusy(false); }
  };

  const active = documents.filter((d) => !d.archived);
  const archived = documents.filter((d) => d.archived);

  const Row = ({ d }) => (
    <div className={`rounded-md border px-3 py-2 ${d.archived ? "border-stone-200 bg-stone-50 opacity-75" : "border-stone-200 bg-white"}`} data-testid={`row-document-${d.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={16} className="text-[#17324D] shrink-0" />
          <div className="min-w-0">
            <div className="text-sm text-stone-800 truncate">{d.filename}</div>
            <div className="text-[11px] text-[#5B6570]">{fmtSize(d.size)} · uploaded {fmtDate(d.uploaded_at)}{d.archived ? " · archived" : ""}</div>
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={downloading === d.id} onClick={() => download(d)} data-testid={`button-download-${d.id}`}>
            <DownloadSimple size={13} className="mr-1" /> {downloading === d.id ? "Preparing…" : "Download"}
          </Button>
          {!d.archived && archiving !== d.id && (
            <Button size="sm" variant="ghost" onClick={() => setArchiving(d.id)} data-testid={`button-archive-${d.id}`}><Archive size={13} className="mr-1" /> Archive</Button>
          )}
        </div>
      </div>
      {archiving === d.id && (
        <>
          <p className="text-[11px] text-[#5B6570] mt-2">Archiving hides this file from the active list. The original is kept privately and can still be downloaded.</p>
          <VoidForm busy={archiveBusy} onCancel={() => setArchiving(null)} onSubmit={(r) => archive(d, r)} />
        </>
      )}
    </div>
  );

  return (
    <div className="mb-5 rounded-md border border-[#C5D3E2] bg-[#F4F7FA] p-4" data-testid="scope-documents">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#17324D]">Brief &amp; scope documents</div>
      <p className="text-xs text-[#5B6570] mt-1">
        Upload the original client brief or scope (PDF, DOCX or DOC, up to 15 MB). Files are private.
        {library ? " Previously uploaded formula briefs also appear here." : " These attachments belong to this formula workspace."}
        {" "}Files are not read by the AI review and no text is extracted from them.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept=".pdf,.docx,.doc,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => pick(e.target.files?.[0])} className="sr-only" id={`scope-file-${recipeId}`} data-testid="input-document-file" />
        <label htmlFor={`scope-file-${recipeId}`} className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-[#17324D] text-[#17324D] bg-white px-3 py-1.5 text-sm hover:bg-[#EAF0F6]" data-testid="button-choose-file">
          <UploadSimple size={14} /> Choose brief or scope
        </label>
        <span className="text-sm text-stone-700 truncate max-w-[16rem]" data-testid="text-selected-file">{file ? `${file.name} (${fmtSize(file.size)})` : "No file selected"}</span>
        <Button size="sm" disabled={!file || uploading} onClick={upload} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="button-upload-document">
          {uploading ? `Uploading ${progress}%` : "Upload"}
        </Button>
      </div>
      {uploading && <div className="mt-2 h-1.5 rounded bg-[#C5D3E2] overflow-hidden"><div className="h-full bg-[#17324D] transition-transform origin-left" style={{ transform: `scaleX(${progress / 100})` }} /></div>}
      <div className="mt-2"><ErrorBox message={error} testId="error-document" /></div>

      <div className="mt-3 space-y-2">
        {active.length === 0 ? <p className="text-sm text-[#5B6570]">No active documents uploaded yet.</p> : active.map((d) => <Row key={d.id} d={d} />)}
      </div>
      {archived.length > 0 && (
        <div className="mt-3">
          <button type="button" className="text-xs underline text-[#5B6570]" onClick={() => setShowArchived((v) => !v)} data-testid="button-toggle-archived">
            {showArchived ? "Hide" : "Show"} archived ({archived.length})
          </button>
          {showArchived && <div className="mt-2 space-y-2">{archived.map((d) => <Row key={d.id} d={d} />)}</div>}
        </div>
      )}
    </div>
  );
}
